import React, { useEffect, useRef, useState } from "react";
import { EditOutlined } from "@ant-design/icons";

const stop = (e) => {
  e.stopPropagation();
};

const EditableLabel = ({
  value,
  onChange,
  editable = true,
  showIcon = true,
  placeholder = "Untitled",
  maxLength,
  /** Strip disallowed chars as the user types (e.g. letters/numbers only). */
  sanitize,
  className,
  inputClassName,
  iconClassName,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value || "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = (e) => {
    if (!editable) return;
    if (e) stop(e);
    setEditing(true);
  };

  const normalize = (text) => {
    let next = sanitize ? sanitize(text ?? "") : text ?? "";
    if (maxLength && next.length > maxLength) next = next.slice(0, maxLength);
    return next;
  };

  const commit = () => {
    const next = normalize((draft || "").trim());
    if (next && next !== value) onChange?.(next);
    else setDraft(value || "");
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className={inputClassName}
        value={draft}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => setDraft(normalize(e.target.value))}
        onBlur={commit}
        onClick={stop}
        onKeyDown={(e) => {
          stop(e);
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") cancel();
        }}
        style={{
          border: "none",
          outline: "none",
          background: "transparent",
          font: "inherit",
          color: "inherit",
          padding: 0,
          minWidth: 80,
        }}
      />
    );
  }

  return (
    <span className={className} onDoubleClick={startEdit}>
      {editable && showIcon && (
        <EditOutlined
          className={iconClassName}
          onClick={startEdit}
          style={{ marginRight: 6, cursor: "pointer", opacity: 0.55 }}
        />
      )}
      {value || placeholder}
    </span>
  );
};

export default EditableLabel;
